/**
 * Copy that differs between markets because of spelling or wording, not because of what a site can
 * do. Anything that changes site *behaviour* belongs in a feature flag or `siteMetadata` instead —
 * see the `siteSettings.lang` rule in `www/AGENTS.md`.
 *
 * Each locale spells its object out in full rather than patching a base, so the difference between
 * two markets is a plain diff of two adjacent literals.
 */

import type { CompareAtPriceLabels } from '@dfh-corp-sites/ui'

import type { ProductVariantAttribute } from '../../graphql/search'
import { langCodes } from '../../utils/siteSettings'

/** Magento variant attribute codes, as they arrive on cart items and `variant_index`. */
export type AttributeCode = 'flavor' | 'size' | 'flavor_size' | 'dosage'

/**
 * Keyed off the Commerce API's generated enum. Unlike a Magento attribute label, this is a fixed set
 * the API defines rather than editor content, so it belongs here — and codegen turns a new attribute
 * into a type error instead of a silently missing label.
 */
export type MultipleAttributeCode = ProductVariantAttribute

/** Wording for the Favorites feature: header menu, popover, account page, PLP filter and toasts. */
export type FavoritesCopy = {
  /**
   * Account page route, relative to `/account`. Only the market's own spelling is routed; the US
   * `/favorites` path redirects to it so older links keep working.
   */
  accountRoute: string
  /** Header trigger and popover heading. */
  menuLabel: string
  closeMenuLabel: string
  goToFavorites: string
  /** Account nav entry and account page heading. */
  myFavorites: string
  retryLoading: string
  itemsOnlyFilter: string
  loggedOutTitle: string
  noResultsTitle: string
  exploreSubtitle: string
  removedToast: (productName?: string | null) => string
  errorToast: (productName?: string | null) => string
  /** Accessible label for the heart toggle on a product card or PDP. */
  toggleLabel: (isFavorite: boolean, productName?: string | null) => string
}

export type Copy = {
  /**
   * Display labels for Magento variant attribute codes. Keyed loosely, so an attribute code we do
   * not recognise falls through to the code itself in `attributeDisplayLabel`.
   */
  attributeLabels: Record<string, string>
  /** Shown on a product card when a product has more than one value for an attribute. */
  multipleAttributeLabels: Record<MultipleAttributeCode, string>
  /** Defensive fallback when a Magento variant carries an attribute value with no label. */
  unknownAttributeLabels: Record<AttributeCode, string>
  /** Screen-reader labels around a struck-through compare-at price and the price charged. */
  compareAtPriceLabels: CompareAtPriceLabels
  featuredProductsDefaultTitle: string
  bannerColorsHeading: string
  licenseNumberLabel: string
  licenseNumberRequiredMessage: string
  /** Heads the dispensary dashboard card that lists the commissions documents still owed. */
  missingCommissionsDocumentsHeading: string
  favorites: FavoritesCopy
}

const enUS: Copy = {
  attributeLabels: {
    flavor: 'Flavor',
    size: 'Size',
    flavor_size: 'Flavor / Size',
    dosage: 'Dosage',
  },
  multipleAttributeLabels: {
    SIZE: 'Multiple sizes',
    FLAVOR: 'Multiple flavors',
    FLAVOR_SIZE: 'Multiple flavor sizes',
    DOSAGE: 'Multiple dosages',
  },
  unknownAttributeLabels: {
    flavor: 'Unknown Flavor',
    size: 'Unknown Size',
    flavor_size: 'Unknown Flavor / Size',
    dosage: 'Unknown Dosage',
  },
  compareAtPriceLabels: {
    originalPrice: 'Original price:',
    currentPrice: 'Current price:',
  },
  featuredProductsDefaultTitle: 'Our favorite Designs for Health products',
  bannerColorsHeading: 'Colors',
  licenseNumberLabel: 'License number',
  licenseNumberRequiredMessage:
    'You are required to provide a license number or upload a certificate.',
  missingCommissionsDocumentsHeading: 'Missing account information',
  favorites: {
    accountRoute: '/favorites',
    menuLabel: 'Favorites',
    closeMenuLabel: 'Close favorites',
    goToFavorites: 'Go to Favorites',
    myFavorites: 'My Favorites',
    retryLoading: 'Retry Loading Favorites',
    itemsOnlyFilter: 'Favorite Items Only',
    loggedOutTitle: 'Log in or create account to save your favorites',
    noResultsTitle: 'No favorites yet',
    exploreSubtitle: 'Explore our catalog and save your favorites.',
    removedToast: (productName) =>
      productName ? `${productName} removed from Favorites` : 'Removed from Favorites',
    errorToast: (productName) =>
      productName
        ? `We couldn't update ${productName}. Please try again.`
        : `We couldn't update your Favorites. Please try again.`,
    toggleLabel: (isFavorite, productName) =>
      isFavorite ? `Remove ${productName} from favorites` : `Add ${productName} to favorites`,
  },
}

const enCA: Copy = {
  attributeLabels: {
    flavor: 'Flavour',
    size: 'Size',
    flavor_size: 'Flavour / Size',
    dosage: 'Dosage',
  },
  multipleAttributeLabels: {
    SIZE: 'Multiple sizes',
    FLAVOR: 'Multiple flavours',
    FLAVOR_SIZE: 'Multiple flavour sizes',
    DOSAGE: 'Multiple dosages',
  },
  unknownAttributeLabels: {
    flavor: 'Unknown Flavour',
    size: 'Unknown Size',
    flavor_size: 'Unknown Flavour / Size',
    dosage: 'Unknown Dosage',
  },
  compareAtPriceLabels: {
    originalPrice: 'Original price:',
    currentPrice: 'Current price:',
  },
  featuredProductsDefaultTitle: 'Our favourite Designs for Health products',
  bannerColorsHeading: 'Colours',
  // "Licence" is the noun in Canadian English; the verb keeps the `s`.
  licenseNumberLabel: 'Licence number',
  licenseNumberRequiredMessage:
    'You are required to provide a licence number or upload a certificate.',
  missingCommissionsDocumentsHeading: 'ACTION REQUIRED',
  favorites: {
    accountRoute: '/favourites',
    menuLabel: 'Favourites',
    closeMenuLabel: 'Close favourites',
    goToFavorites: 'Go to Favourites',
    myFavorites: 'My Favourites',
    retryLoading: 'Retry Loading Favourites',
    itemsOnlyFilter: 'Favourite Items Only',
    loggedOutTitle: 'Log in or create account to save your favourites',
    noResultsTitle: 'No favourites yet',
    exploreSubtitle: 'Explore our catalogue and save your favourites.',
    removedToast: (productName) =>
      productName ? `${productName} removed from Favourites` : 'Removed from Favourites',
    errorToast: (productName) =>
      productName
        ? `We couldn't update ${productName}. Please try again.`
        : `We couldn't update your Favourites. Please try again.`,
    toggleLabel: (isFavorite, productName) =>
      isFavorite ? `Remove ${productName} from favourites` : `Add ${productName} to favourites`,
  },
}

const copyByLang: Record<string, Copy> = {
  [langCodes.en_US]: enUS,
  [langCodes.en_CA]: enCA,
}

/**
 * Resolve copy for a `siteSettings.lang` value, falling back to US English for an unset or
 * unrecognised locale. Returns a shared constant, so the result is referentially stable and needs
 * no memoization at the call site.
 */
export const copyFor = (lang?: string | null): Copy => copyByLang[lang ?? ''] ?? enUS

/**
 * Display label for a variant attribute code. Deliberately resolved from the table above rather than
 * from Magento's own `configurable_options.label`: the wording is effectively fixed UI vocabulary, so
 * pinning it here keeps it reviewed and identical across every surface, including the PLP grid card,
 * whose Commerce API data carries no label to read. An attribute code with no entry falls through, so
 * add one here when the catalog gains an attribute.
 */
export const attributeDisplayLabel = (copy: Copy, code: string): string =>
  copy.attributeLabels[code] ?? code
